import styled from "styled-components";
import {
  GRIFFLE_COLORS,
  GRIFFLE_FONT,
  GRIFFLE_FONT_BODY,
  GRIFFLE_TYPE,
  GRIFFLE_RADIUS,
  GRIFFLE_SHADOW,
  GRIFFLE_SPACE,
  GRIFFLE_STAT_COLORS
} from "../griffleTheme";

export const StyledGriffleDashboard = styled.div`
  padding: ${GRIFFLE_SPACE.pagePadding};
  font-family: ${GRIFFLE_FONT_BODY};
  max-width: 1280px;

  /* ── Header: greeting + inline stats ────────────────────── */
  .hero {
    margin-bottom: 24px;
  }

  .page-title {
    font-family: ${GRIFFLE_FONT};
    font-weight: ${GRIFFLE_TYPE.weightBold};
    font-size: ${GRIFFLE_TYPE.headingLg};
    color: ${GRIFFLE_COLORS.ink};
    line-height: 1.2;
    margin: 0 0 10px;
  }

  .hero-stats {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .hero-stat {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: ${GRIFFLE_TYPE.bodyMd};
    color: ${GRIFFLE_COLORS.inkMuted};

    svg {
      color: ${GRIFFLE_COLORS.primary};
      flex-shrink: 0;
    }

    strong {
      font-weight: ${GRIFFLE_TYPE.weightBold};
      color: ${GRIFFLE_COLORS.ink};
    }
  }

  .hero-stat-divider {
    width: 1px;
    height: 16px;
    background: ${GRIFFLE_COLORS.border};
    flex-shrink: 0;
  }

  /* ── Action cards ──────────────────────────────────────── */
  .action-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 28px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  .action-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    background: ${GRIFFLE_COLORS.surface};
    border: 1px solid ${GRIFFLE_COLORS.border};
    border-radius: ${GRIFFLE_RADIUS.card};
    box-shadow: ${GRIFFLE_SHADOW.card};
    text-decoration: none;
    color: inherit;
    transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${GRIFFLE_SHADOW.cardHover};
      text-decoration: none;
      color: inherit;
    }
  }

  .action-card-icon {
    width: 42px;
    height: 42px;
    border-radius: ${GRIFFLE_RADIUS.button};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .action-card--create .action-card-icon {
    background: ${GRIFFLE_STAT_COLORS.students.bg};
    color: ${GRIFFLE_COLORS.primary};
  }
  .action-card--create:hover {
    border-color: ${GRIFFLE_COLORS.primary};
  }

  .action-card--overdue .action-card-icon {
    background: ${GRIFFLE_STAT_COLORS.minutes.bg};
    color: ${GRIFFLE_STAT_COLORS.minutes.accent};
  }
  .action-card--overdue:hover {
    border-color: ${GRIFFLE_STAT_COLORS.minutes.accent};
  }

  .action-card--attention .action-card-icon {
    background: ${GRIFFLE_STAT_COLORS.books.bg};
    color: ${GRIFFLE_STAT_COLORS.books.accent};
  }
  .action-card--attention:hover {
    border-color: ${GRIFFLE_STAT_COLORS.books.accent};
  }

  .action-card-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .action-card-title {
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    font-size: ${GRIFFLE_TYPE.body};
    color: ${GRIFFLE_COLORS.ink};
  }

  .action-card-desc {
    font-size: ${GRIFFLE_TYPE.small};
    color: ${GRIFFLE_COLORS.inkMuted};
  }

  /* ── Upcoming Assignments spacing ─────────────────────── */
  > div:has(.ua-header) {
    margin-top: 24px;
  }

  /* ── Toggle details button ─────────────────────────────── */
  .toggle-details-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 24px;
    padding: 8px 0;
    background: none;
    border: none;
    cursor: pointer;
    font-family: ${GRIFFLE_FONT_BODY};
    font-size: ${GRIFFLE_TYPE.bodyMd};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    color: ${GRIFFLE_COLORS.primary};

    &:hover {
      text-decoration: underline;
    }
  }

  .toggle-chevron {
    transition: transform 0.2s;
  }
  .toggle-chevron.open {
    transform: rotate(180deg);
  }

  /* ── Expandable details ────────────────────────────────── */
  .details-section {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* ── Charts ────────────────────────────────────────────── */
  .chart-panel {
    background: ${GRIFFLE_COLORS.surface};
    border: 1px solid ${GRIFFLE_COLORS.border};
    border-radius: ${GRIFFLE_RADIUS.card};
    padding: 24px;
    box-shadow: ${GRIFFLE_SHADOW.card};
  }

  .chart-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .chart-panel-title {
    font-family: ${GRIFFLE_FONT};
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    font-size: ${GRIFFLE_TYPE.headingMd};
    color: ${GRIFFLE_COLORS.ink};
    margin: 0;
  }

  .chart-panel-subtitle {
    font-size: ${GRIFFLE_TYPE.small};
    color: ${GRIFFLE_COLORS.inkMuted};
    margin: 2px 0 0;
  }

  .chart-body {
    height: 320px;
    position: relative;
  }

  /* ── Empty state ──────────────────────────────────────── */
  .empty-chart {
    height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: ${GRIFFLE_COLORS.inkMuted};
    font-size: ${GRIFFLE_TYPE.body};
    border: 2px dashed ${GRIFFLE_COLORS.border};
    border-radius: ${GRIFFLE_RADIUS.button};
  }

  .empty-chart-icon {
    font-size: 28px;
    opacity: 0.4;
  }
`;
